/**
 * Catálogo de itens cotáveis e resultado de um orçamento, compartilhado entre
 * backend e frontend.
 *
 * Regra de ouro: nada aqui carrega preço vindo do cliente ou de um LLM. Quem
 * pede um orçamento informa apenas `{codigo, quantidade}` (`ItemSolicitado`);
 * preço unitário vem da fonte configurada e desconto vem de regra cadastrada,
 * ambos resolvidos no servidor. É o mesmo princípio do campo `arquivo` do mini
 * formulário, que só aceita o id do upload e resolve os metadados no servidor.
 */

/** Item do catálogo já normalizado, seja qual for a fonte. */
export interface ItemCatalogo {
  /** Identidade estável do item dentro do catálogo. */
  codigo: string;
  nome: string;
  descricao?: string;
  /** Preço unitário na moeda do catálogo. */
  preco: number;
  unidade?: string;
  metadados?: Record<string, any>;
}

/**
 * De onde sai cada atributo do item na fonte: nome de campo (dataset) ou
 * JSONPath relativo ao item (integração).
 */
export interface MapaCamposCatalogo {
  codigo: string;
  nome: string;
  preco: string;
  descricao?: string;
  unidade?: string;
}

/** Itens vêm de uma tabela de dados do tenant. */
export interface CatalogoDataset {
  fonte: "dataset";
  dataset_chave: string;
  mapa: MapaCamposCatalogo;
}

/**
 * Itens vêm da tabela `produto` do CRM.
 *
 * O mapa é opcional porque `produto` tem colunas fixas com um padrão óbvio
 * (ver `MAPA_PADRAO_PRODUTO`). Ele existe para o caso em que o dado real mora
 * nos metadados do tenant — um SKU em `metadados.sku`, por exemplo.
 */
export interface CatalogoProduto {
  fonte: "produto";
  mapa?: MapaCamposCatalogo;
}

/** Colunas de `produto` usadas quando o catálogo não define um mapa. */
export const MAPA_PADRAO_PRODUTO: MapaCamposCatalogo = {
  codigo: "id",
  nome: "nome",
  preco: "valor",
  descricao: "descricao",
};

/** Prefixo de chave que aponta para dentro do jsonb `metadados` do produto. */
export const PREFIXO_METADADOS = "metadados.";

/** Itens vêm de uma ação de Integration (ERP externo). */
export interface CatalogoIntegration {
  fonte: "integration";
  integrationId: number;
  /** "custom" ou a chave do template. */
  template_key: string;
  template_version?: string;
  /** Ação da integração que lista os itens. */
  action: string;
  /** Parâmetros fixos passados à ação. */
  paramsFixos?: Record<string, string>;
  /** JSONPath do array de itens no resultado. */
  jsonpathLista: string;
  /** JSONPath de cada atributo, relativo ao item da lista. */
  jsonpath: MapaCamposCatalogo;
}

export type CatalogoConfig =
  | CatalogoDataset
  | CatalogoProduto
  | CatalogoIntegration;

export type FonteCatalogo = CatalogoConfig["fonte"];

/** Catálogo como trafega entre backend e frontend. */
export interface Catalogo {
  id?: number;
  /** Identidade estável: nós de fluxo, agentes e campos de formulário apontam para ela. */
  chave: string;
  nome: string;
  descricao?: string;
  config?: CatalogoConfig;
  moeda?: string;
  /** Dias de validade do orçamento gerado. Nulo = sem prazo. */
  validade_dias?: number | null;
  ativo?: boolean;
}

/**
 * Um campo que a fonte oferece para o mapeamento. `chave` é o que vai gravado
 * no mapa: nome de campo (dataset/produto) ou JSONPath relativo ao item
 * (integração).
 */
export interface EstruturaCampoCatalogo {
  chave: string;
  rotulo: string;
  tipo?: string;
}

/**
 * O que a fonte expõe, para o formulário conciliar os atributos do catálogo
 * com os campos reais — em vez de o admin digitar nomes e JSONPaths de cabeça.
 */
export interface EstruturaCatalogo {
  campos: EstruturaCampoCatalogo[];
  /**
   * Como a lista foi obtida. `schema` = declarada pela fonte (dataset,
   * produto); `amostra` = inferida de um registro real devolvido pela fonte
   * (integração), e portanto só mostra o que veio naquele item.
   */
  origem: "schema" | "amostra";
  /** Integração: saídas que contêm uma lista, candidatas a `jsonpathLista`. */
  listas?: EstruturaCampoCatalogo[];
  /** Motivo de a estrutura ter vindo vazia, para o formulário explicar. */
  aviso?: string;
}

/** Linha pedida por quem solicita o orçamento — o único dado que vem de fora. */
export interface ItemSolicitado {
  codigo: string;
  quantidade: number;
}

/** Linha já precificada pelo servidor. */
export interface ItemCalculado {
  codigo: string;
  nome: string;
  descricao?: string;
  unidade?: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
}

export type OrigemDesconto = "cupom" | "cliente" | "marcador";

/**
 * O desconto que venceu. Descontos NÃO acumulam: entre o cupom e a regra de
 * cliente/marcador vale o de maior valor absoluto (ver `OrcamentoService`).
 */
export interface DescontoAplicado {
  origem: OrigemDesconto;
  /** Texto exibível de qual regra venceu (ex.: 'Cupom PROMO10 (10%)'). */
  descricao: string;
  percentual?: number;
  valor: number;
}

export interface OrcamentoCalculado {
  catalogo_chave: string;
  moeda: string;
  itens: ItemCalculado[];
  subtotal: number;
  desconto: DescontoAplicado | null;
  total: number;
  valido_ate?: string;
}

export type OrcamentoStatus =
  | "rascunho"
  | "enviado"
  | "aceito"
  | "recusado"
  | "expirado"
  | "cancelado";

export type OrcamentoOrigem = "ia" | "fluxo" | "formulario" | "manual";

export type TipoCupom = "percentual" | "valor";

export type EscopoRegraDesconto = "marcador" | "cliente";

export const MOEDA_PADRAO = "BRL";

/**
 * Unidade exibida quando a fonte não tem uma.
 *
 * É padrão de EXIBIÇÃO, não de dado: o mapeamento em branco continua
 * significando "a fonte não informa unidade", e nada é gravado no item. Assim
 * mudar a convenção depois não reescreve o que já foi cotado.
 */
export const UNIDADE_PADRAO = "UN";

/**
 * Aritmética de dinheiro em centavos inteiros. Aplicar percentual sobre floats
 * acumula erro (0.1 + 0.2 !== 0.3); todo o cálculo do orçamento roda em
 * centavos e só volta para unidades monetárias na borda.
 */
export function paraCentavos(valor: number): number {
  return Math.round(valor * 100);
}

export function deCentavos(centavos: number): number {
  return Math.round(centavos) / 100;
}

export function formatarMoeda(valor: number, moeda = MOEDA_PADRAO): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: moeda }).format(valor);
}
